import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { disabled, form, FormField } from '@angular/forms/signals';
import { MatButton } from '@angular/material/button';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatDivider } from '@angular/material/divider';
import {
  UpdateUserEmailNotificationsDto,
  UserEmailNotificationsDto,
  ZoudashUtils,
} from '@workspace/data-types';
import { AbstractSignalFormComponent, AlertSnackBarService } from '@workspace/ng-aura';
import { UserStore } from '@workspace/ng-data-access';
import { ApiService } from '@workspace/ng-http';
import { catchError, EMPTY, finalize, tap } from 'rxjs';

@Component({
  selector: 'core-user-notifications',
  imports: [FormField, MatDivider, MatButton, MatCheckbox],
  templateUrl: './user-notifications.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserNotificationsCoreComponent extends AbstractSignalFormComponent<UserEmailNotificationsDto> {
  private readonly userStore = inject(UserStore);
  private readonly api = inject(ApiService);
  private readonly alertSnackbar = inject(AlertSnackBarService);

  readonly title = input<string>('Notifications');

  readonly currentUser = this.userStore.currentUser;
  readonly isSaving = signal<boolean>(false);

  override model: WritableSignal<UserEmailNotificationsDto> = signal<UserEmailNotificationsDto>({
    securityAlerts: true,
    chatMessages: true,
    propertyActivity: true,
    productUpdates: false,
    weeklyDigest: false,
  });

  override formTree = form(this.model, (b) => {
    disabled(b.securityAlerts, { when: () => this.isSaving() || this.allDisable() });
    disabled(b.chatMessages, { when: () => this.isSaving() || this.allDisable() });
    disabled(b.propertyActivity, { when: () => this.isSaving() || this.allDisable() });
    disabled(b.productUpdates, { when: () => this.isSaving() || this.allDisable() });
    disabled(b.weeklyDigest, { when: () => this.isSaving() || this.allDisable() });
  });

  readonly isDirty = computed(() => {
    if (!this.formReady() || !this.ogValue) {
      return false;
    }
    return !ZoudashUtils.isEqual(this.model(), this.ogValue);
  });

  constructor() {
    super();

    effect(() => {
      const user = this.currentUser();
      const emailSettings = user?.settings?.notifications?.email;
      if (emailSettings && !this.dto()) {
        const initialDto: UserEmailNotificationsDto = {
          securityAlerts: emailSettings.securityAlerts ?? true,
          chatMessages: emailSettings.chatMessages ?? true,
          propertyActivity: emailSettings.propertyActivity ?? true,
          productUpdates: emailSettings.productUpdates ?? false,
          weeklyDigest: emailSettings.weeklyDigest ?? false,
        };
        this.ogValue = structuredClone(initialDto);
        this.model.set(initialDto);
        this.formReady.set(true);
      }
    });
  }

  override buildModelFromDto(dto: UserEmailNotificationsDto): UserEmailNotificationsDto {
    return {
      securityAlerts: dto.securityAlerts ?? true,
      chatMessages: dto.chatMessages ?? true,
      propertyActivity: dto.propertyActivity ?? true,
      productUpdates: dto.productUpdates ?? false,
      weeklyDigest: dto.weeklyDigest ?? false,
    };
  }

  onCancel(): void {
    this.resetForm();
  }

  onSave(): void {
    if (!this.isDirty() || this.isSaving()) {
      return;
    }

    const payload: UpdateUserEmailNotificationsDto = this.model();
    this.isSaving.set(true);

    this.api
      .user()
      .settings()
      .notifications()
      .updateEmailNotifications(payload)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        tap((updated: UserEmailNotificationsDto) => {
          this.userStore.updateCurrentUserEmailNotifications(updated);
          this.ogValue = structuredClone(updated);
          this.model.set(structuredClone(updated));
          this.alertSnackbar.success({
            title: 'Préférences enregistrées',
            message: 'Vos préférences de notifications ont été mises à jour avec succès.',
          });
        }),
        catchError(() => {
          this.alertSnackbar.error({
            title: 'Erreur',
            message: 'Impossible de mettre à jour vos préférences de notifications.',
          });
          return EMPTY;
        }),
        finalize(() => {
          this.isSaving.set(false);
        }),
      )
      .subscribe();
  }
}
